import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import CardDetailModal from '../components/CardDetailModal';
import { Search, Building2, Users, Mail, Calendar, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NurturingList = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showRetiredOnly, setShowRetiredOnly] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/nurturing`;
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCompanies();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchCompanies]);

  const handleCardUpdate = () => {
    fetchCompanies();
  };

  const getMainContact = (card) => {
    const mainContact = card.key_people?.find(p => p.role === 'main_contact');
    return mainContact || card.key_people?.[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredCompanies = showRetiredOnly 
    ? companies.filter(c => c.is_retired)
    : companies;

  if (loading) {
    return (
      <AppLayout title="Companies">
        <div className="text-[#A2FF00]">Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Companies">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Companies
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>View and manage all companies</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/member-tracker')}
            className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 mb-6`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by company name, contact name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm pl-10 pr-4 py-2 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none`}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showRetiredOnly}
              onChange={(e) => setShowRetiredOnly(e.target.checked)}
              className={`w-4 h-4 rounded ${isDark ? 'border-white/20 bg-black' : 'border-gray-300 bg-white'} text-[#A2FF00] focus:ring-[#A2FF00]`}
            />
            <span className="text-gray-400 text-sm">Show retired only</span>
          </label>
          <button
            onClick={fetchCompanies}
            className={`p-2 ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-500 hover:text-green-600'} transition-colors`}
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-3 text-sm text-gray-500">
          {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} found
          {showRetiredOnly && ' (retired only)'}
        </div>
      </div>

      {/* Company List */}
      {filteredCompanies.length === 0 ? (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 text-center`}>
          <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            {searchTerm ? 'No companies match your search' : 'No companies in the database yet'}
          </p>
        </div>
      ) : (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <tr>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Company</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Main Contact</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Last Updated</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                const mainContact = getMainContact(company);
                return (
                  <tr
                    key={company.card_id}
                    onClick={() => setSelectedCard(company)}
                    className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer transition-colors`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-sm ${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-100'} flex items-center justify-center flex-shrink-0`}>
                          <Building2 className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{company.title}</div>
                          {company.website_url && (
                            <div className="text-xs text-gray-500 truncate max-w-[200px]">
                              {company.website_url}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {mainContact ? (
                        <div>
                          <div className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm flex items-center gap-1`}>
                            <Users className="w-3 h-3 text-gray-400" />
                            {[mainContact.first_name, mainContact.last_name].filter(Boolean).join(' ') || 'No name'}
                          </div>
                          {mainContact.email && (
                            <div className="text-gray-500 text-xs flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {mainContact.email}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No contact</span>
                      )}
                    </td>
                    <td className="p-4">
                      {company.is_retired ? (
                        <div>
                          <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-400">
                            Retired
                          </span>
                          {company.retired_reason && (
                            <div className="text-xs text-gray-500 mt-1 capitalize">
                              {company.retired_reason.replace(/_/g, ' ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-gray-400 text-sm flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(company.updated_at)}
                      </div>
                    </td>
                    <td className="p-4">
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          boardType="member_tracking"
          columnName={null}
        />
      )}
    </AppLayout>
  );
};

export default NurturingList;
